import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type { MnjOperacaoWithItens } from "@shared/schema";
import { calcularDivergencia, resumirDivergencia } from "@shared/manejo-divergencia";
import { useFuncionarios } from "@/hooks/use-equipe";
import { Check, AlertTriangle } from "lucide-react";
import { tituloDaOperacao } from "./card-operacao";

// Folha de conclusão — spec §5/§6.1.
//
// O caso comum é CONFIRMAR, não preencher: a folha abre com o previsto em todos os
// campos e com quem estava atribuído já marcado, e um toque em "Confirmar
// conclusão" grava. Quem aplicou diferente corrige o campo; quem aplicou o
// planejado não digita nada.
//
// Bottom sheet no celular (`side="bottom"`, sobe do polegar) e painel lateral no
// desktop. É UMA chamada ao `/concluir` — ver o porquê em server/storage/manejo.ts.

export interface FolhaConclusaoProps {
  operacao: MnjOperacaoWithItens | null;
  onOpenChange: (aberta: boolean) => void;
  onConfirmar: (dados: {
    itens: Array<{ mnjProdutoId: string; qtdRealizada: string }>;
    executores: string[];
    observacao: string | null;
  }) => void;
  salvando?: boolean;
}

export function FolhaConclusao({ operacao, onOpenChange, onConfirmar, salvando }: FolhaConclusaoProps) {
  const { data: funcionarios = [] } = useFuncionarios();
  const [quantidades, setQuantidades] = useState<Record<string, string>>({});
  const [executores, setExecutores] = useState<string[]>([]);
  const [observacao, setObservacao] = useState("");

  // Repõe os padrões a cada operação aberta. Sem isto, concluir a segunda
  // operação herdaria os números digitados na primeira — e ninguém confere um
  // campo que já veio preenchido "certo".
  useEffect(() => {
    if (!operacao) return;
    const iniciais: Record<string, string> = {};
    for (const it of operacao.itens) {
      iniciais[it.mnjProdutoId] = String(it.qtdRealizada ?? it.qtdPrevista);
    }
    setQuantidades(iniciais);
    setExecutores(operacao.executores.filter(e => e.papel === "atribuido").map(e => e.funcionarioId));
    setObservacao("");
  }, [operacao?.id]);

  if (!operacao) return null;

  const itensParaDivergencia = operacao.itens.map(it => ({
    mnjProdutoId: it.mnjProdutoId,
    produtoNome: it.produto.nome,
    unidade: it.produto.unidade,
    qtdPrevista: Number(it.qtdPrevista),
    qtdRealizada: parseFloat((quantidades[it.mnjProdutoId] ?? "").replace(",", ".")),
  })).map(it => ({ ...it, qtdRealizada: isNaN(it.qtdRealizada) ? null : it.qtdRealizada }));

  // Selo AO VIVO — o aviso aparece enquanto a pessoa digita, nunca depois de
  // gravar. E nunca desabilita o botão: divergir é informação, não portão (§5).
  const divergencia = calcularDivergencia(itensParaDivergencia);
  const aviso = resumirDivergencia(divergencia);

  const alternarExecutor = (id: string) =>
    setExecutores(atuais => atuais.indexOf(id) === -1 ? [...atuais, id] : atuais.filter(e => e !== id));

  const confirmar = () => {
    onConfirmar({
      itens: operacao.itens.map(it => ({
        mnjProdutoId: it.mnjProdutoId,
        qtdRealizada: (quantidades[it.mnjProdutoId] ?? String(it.qtdPrevista)).replace(",", "."),
      })),
      executores,
      observacao: observacao.trim() || null,
    });
  };

  return (
    <Sheet open={!!operacao} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[92vh] overflow-y-auto sm:max-w-lg sm:mx-auto rounded-t-xl"
      >
        <SheetHeader className="text-left">
          <SheetTitle>Concluir operação</SheetTitle>
          <SheetDescription className="truncate">{tituloDaOperacao(operacao)}</SheetDescription>
        </SheetHeader>

        <div className="space-y-5 py-4">
          {operacao.itens.length > 0 && (
            <div className="space-y-2">
              <Label>Quantidades aplicadas</Label>
              {operacao.itens.map(it => (
                <div key={it.id} className="flex items-center gap-2">
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-sm">{it.produto.nome}</span>
                    <span className="block text-xs text-muted-foreground tabular-nums">
                      previsto {Number(it.qtdPrevista).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}
                    </span>
                  </span>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    className="w-28 min-h-11 text-right tabular-nums"
                    value={quantidades[it.mnjProdutoId] ?? ""}
                    onChange={e => setQuantidades(q => ({ ...q, [it.mnjProdutoId]: e.target.value }))}
                    data-testid={`qtd-${it.mnjProdutoId}`}
                  />
                  {/* A unidade FORA do campo: dentro, ela some quando o número é
                      longo, e é a unidade que diz se 4000 é muito ou pouco. */}
                  <span className="w-8 shrink-0 text-sm text-muted-foreground">{it.produto.unidade}</span>
                </div>
              ))}
            </div>
          )}

          {aviso && (
            <p className="flex items-start gap-2 rounded-md border border-atencao/30 bg-atencao-suave p-2.5 text-sm text-atencao">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {aviso}. Pode concluir assim mesmo — a operação fica com um selo de divergência
                para conferência depois.
              </span>
            </p>
          )}

          <div className="space-y-2">
            <Label>Quem executou</Label>
            <div className="flex flex-wrap gap-1.5">
              {funcionarios.map(f => {
                const marcado = executores.indexOf(f.id) !== -1;
                return (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => alternarExecutor(f.id)}
                    data-testid={`executor-${f.id}`}
                    className={cn(
                      "min-h-11 rounded-md border px-3 text-sm transition-colors",
                      marcado ? "border-primary bg-primary/10 font-semibold" : "hover:bg-muted/40",
                    )}
                  >
                    {f.apelido ?? f.nome}
                  </button>
                );
              })}
            </div>
            {executores.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Ninguém marcado — dá para concluir assim, mas aí não fica registrado quem fez.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="obs-conclusao">Observação (opcional)</Label>
            <Textarea
              id="obs-conclusao"
              rows={2}
              placeholder="Faltou pressão no setor B2, apliquei só metade…"
              value={observacao}
              onChange={e => setObservacao(e.target.value)}
            />
          </div>
        </div>

        {/* Sticky: no celular a lista de executores empurra o botão para fora da
            dobra, e o botão principal não pode exigir rolagem. */}
        <div className="sticky bottom-0 -mx-6 border-t bg-background px-6 py-3">
          <Button
            className="w-full min-h-11 bg-info text-white hover:bg-info/90"
            onClick={confirmar}
            disabled={salvando}
            data-testid="btn-confirmar-conclusao"
          >
            <Check className="h-4 w-4 mr-1.5" />
            {salvando ? "Concluindo..." : "Confirmar conclusão"}
          </Button>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            <Badge variant="outline" className="font-normal">1 toque</Badge>{" "}
            confirma o previsto — mude só o que foi diferente.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
